import { Engine } from '@babylonjs/core/Engines/engine';
import { Effect } from '@babylonjs/core/Materials/effect';
import type { Material } from '@babylonjs/core/Materials/material';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures';
import { Color3, Color4 } from '@babylonjs/core/Maths';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { PostProcess } from '@babylonjs/core/PostProcesses/postProcess';
import { PostProcessRenderEffect } from '@babylonjs/core/PostProcesses/RenderPipeline/postProcessRenderEffect';
import { PostProcessRenderPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/postProcessRenderPipeline';
import type { Scene } from '@babylonjs/core/scene';
import type { Nullable } from '@babylonjs/core/types';
import type { Observer } from '@babylonjs/core/Misc/observable';

import type { SceneNode } from '@kb3d/models/classes/scene-node';
import { CameraTools } from './camera-tools';
import edgeRendererFragmentShader from './shaders/edgerenderer-fragment-shader.fx';
import faceColorFragmentShader from './shaders/facecolor-fragment-shader.fx';
import faceColorVertexShader from './shaders/facecolor-vertex-shader.fx';

// --- Register shaders ---
Effect.ShadersStore['faceColorVertexShader'] = faceColorVertexShader;
Effect.ShadersStore['faceColorFragmentShader'] = faceColorFragmentShader;
Effect.ShadersStore['edgeRendererFragmentShader'] = edgeRendererFragmentShader;

// Track both visible/hidden mats so we can cleanly dispose them.
type OverlayMats = { vis: ShaderMaterial; hid?: ShaderMaterial };

export class EdgeRenderer {
  private scene: Scene;
  private engine: Engine;

  private overlays: Map<Mesh, OverlayMats> = new Map();

  private rttVisible: RenderTargetTexture;
  private rttHidden: RenderTargetTexture;
  private rttHiddenDepthTexture: any;

  private postProcess: PostProcess;
  public edgeRendererPipeline: PostProcessRenderPipeline;

  // Hidden-pass backside flip
  private hiddenMeshes = new Set<Mesh>();
  private originalSideByMesh = new WeakMap<Mesh, Nullable<number>>();

  // Restore on mode switch
  private originalLayerMaskByMesh = new WeakMap<Mesh, number>();
  private originalVisibilityByMesh = new WeakMap<Mesh, number>();

  // Per-mesh beforeRender observer to avoid stacking
  private beforeRenderObsByMesh = new WeakMap<Mesh, Nullable<Observer<Mesh>>>();

  // Resize observer for cleanup
  private resizeObserver: Nullable<Observer<any>> = null;

  constructor(scene: Scene, cameraManager: CameraTools, kbScene: SceneNode) {
    this.scene = scene;
    this.engine = scene.getEngine();

    const sceneWidth = this.engine.getRenderWidth();
    const sceneHeight = this.engine.getRenderHeight();

    // --- RTTs ---
    this.rttVisible = new RenderTargetTexture(
      'edgeRTT_visible',
      { width: sceneWidth, height: sceneHeight },
      scene,
      { samples: 1 }
    );
    this.rttVisible.clearColor = new Color4(0, 0, 0, 1);
    this.rttVisible.renderList = [];

    this.rttHidden = new RenderTargetTexture(
      'edgeRTT_hidden',
      { width: sceneWidth, height: sceneHeight },
      scene,
      { samples: 1 }
    );
    this.rttHidden.clearColor = new Color4(0, 0, 0, 1);
    this.rttHidden.renderList = [];

    // Depth for hidden pass (for future depth heuristics; bound to shader)
    this.rttHiddenDepthTexture = this.rttHidden.createDepthStencilTexture();

    this.scene.customRenderTargets.push(this.rttVisible);
    this.scene.customRenderTargets.push(this.rttHidden);

    // --- PostProcess ---
    this.postProcess = new PostProcess(
      'edgePP',
      'edgeRenderer', // fragment shader name in Effect.ShadersStore
      [
        'uSampleRatio',
        'uThickness',
        'uThreshold',
        'screenSize',
        'uEdgeColor',
        'debug',
        'uEnableHiddenEdges',
        'uDashPixels',
        'uHiddenGate',
        'uVisibleGate',
        'uScreenOrigin',
        'uCreaseMinDeg',
        'uCreaseMaxDeg',
        'uRttTexelSize',
        // Anti-resonant dash params
        'uBullseyePeriod',
        'uBullseyeDuty',
        'uDashMix',
      ],
      [
        'uFaceNormalsTexture',
        'uFaceNormalsHiddenTexture',
        'uHiddenDepthTexture',
      ],
      1.0,
      null,
      undefined,
      this.engine,
      true
    );
    this.postProcess.samples = 4;

    // --- Uniform binding ---
    this.postProcess.onApply = (effect) => {
      const edgeColor = new Color3(
        kbScene.edgeColor?.r ?? 1.0,
        kbScene.edgeColor?.g ?? 1.0,
        kbScene.edgeColor?.b ?? 1.0
      );

      const sceneWidthNow = this.engine.getRenderWidth();
      const sceneHeightNow = this.engine.getRenderHeight();
      const rttSize = this.rttVisible.getSize();
      const rttWidthNow = rttSize.width;
      const rttHeightNow = rttSize.height;

      const sceneAspect = sceneWidthNow / Math.max(1, sceneHeightNow);
      const rttAspect = rttWidthNow / Math.max(1, rttHeightNow);

      // Core
      effect.setFloat2('uSampleRatio', sceneAspect / rttAspect, 1.0);

      // Thickness from displayEdgeWidth (pixels)
      const w = Math.max(1, Number(kbScene.edgeWidth ?? 1));
      effect.setFloat('uThickness', w);

      // Kept for compat (not used in shader logic)
      const thresholdCompat = 1 - (kbScene.edgeDisplayTolerance ?? 0.0);
      effect.setFloat('uThreshold', thresholdCompat);

      effect.setFloat2('screenSize', sceneWidthNow, sceneHeightNow);
      effect.setColor3('uEdgeColor', edgeColor);

      // Bind RTTs
      effect.setTexture('uFaceNormalsTexture', this.rttVisible);
      effect.setTexture('uFaceNormalsHiddenTexture', this.rttHidden);
      effect.setTexture('uHiddenDepthTexture', this.rttHiddenDepthTexture);

      // Anti-resonant dash
      effect.setFloat('uDashPixels', 12.0);       // diagonal half-period (px)
      effect.setFloat('uBullseyePeriod', 24.0);   // full ring period (≈ 2 * uDashPixels)
      effect.setFloat('uBullseyeDuty', 0.50);     // 50% on/off
      effect.setFloat('uDashMix', 0.18);          // small radial wobble

      // Centered origin for symmetry
      effect.setFloat2('uScreenOrigin', sceneWidthNow * 0.5, sceneHeightNow * 0.5);

      // Crease min from cosine threshold (displayEdgeTolerance ∈ [0,1])
      const rawTol = Math.min(1, Math.max(0, kbScene.edgeDisplayTolerance ?? 0));
      const minDeg = Math.acos(rawTol) * (180 / Math.PI);
      effect.setFloat('uCreaseMinDeg', minDeg);
      effect.setFloat('uCreaseMaxDeg', 180.0);

      // Gates (keep fixed unless you want single-knob control)
      effect.setFloat('uHiddenGate', 0.10);
      effect.setFloat('uVisibleGate', 0.20);

      // Texel size for neighborhood sampling
      effect.setFloat2('uRttTexelSize', 1.0 / Math.max(1, rttWidthNow), 1.0 / Math.max(1, rttHeightNow));

      effect.setInt('uEnableHiddenEdges', 1);
      effect.setBool('debug', false);
    };

    const edgeRendererEffect = new PostProcessRenderEffect(
      this.engine,
      'edgeRendererEffect',
      () => [this.postProcess],
      false
    );

    this.edgeRendererPipeline = new PostProcessRenderPipeline(
      this.engine,
      'edgeRendererPipeline'
    );
    this.edgeRendererPipeline.addEffect(edgeRendererEffect);

    this.scene.postProcessRenderPipelineManager.addPipeline(this.edgeRendererPipeline);
    cameraManager.setPipeline(this.edgeRendererPipeline);

    // --- Hidden pass: flip side orientation to BACKSIDE during hidden RTT render only ---
    this.rttHidden.onBeforeRenderObservable.add(() => {
      if (!this.rttHidden.renderList || this.hiddenMeshes.size === 0) return;
      this.hiddenMeshes.forEach((m) => {
        try {
          const original = (m as any).overrideMaterialSideOrientation ?? null;
          this.originalSideByMesh.set(m, original);
          (m as any).overrideMaterialSideOrientation = Mesh.BACKSIDE;
        } catch {
          /* ignore if property missing */
        }
      });
    });

    this.rttHidden.onAfterRenderObservable.add(() => {
      if (this.hiddenMeshes.size === 0) return;
      this.hiddenMeshes.forEach((m) => {
        try {
          const original = this.originalSideByMesh.get(m);
          (m as any).overrideMaterialSideOrientation = original ?? null;
        } catch {
          /* ignore */
        }
      });
    });

    // --- Resize: keep RTTs/depth in sync ---
    this.resizeObserver = this.engine.onResizeObservable.add(() => {
      const newW = this.engine.getRenderWidth();
      const newH = this.engine.getRenderHeight();

      try { this.rttVisible.resize({ width: newW, height: newH }); } catch {}
      try { this.rttHidden.resize({ width: newW, height: newH }); } catch {}

      try {
        if (this.rttHiddenDepthTexture && this.rttHiddenDepthTexture.dispose) {
          this.rttHiddenDepthTexture.dispose();
        }
        this.rttHiddenDepthTexture = this.rttHidden.createDepthStencilTexture();
      } catch (e) {
        console.warn('EdgeRenderer.resize: failed to recreate depth-stencil texture', e);
      }
    });
  }

  /**
   * Add a mesh to the edge renderer
   */
  public add(mesh: Mesh, hideMesh: boolean = false, hiddenEdges: boolean = false) {
    if (mesh.isDisposed() || !mesh.isEnabled()) return;
    if (mesh.visibility === 0) {
      console.warn(`EdgeRenderer.add: mesh "${mesh.name}" has visibility=0; proceeding for edge RTTs.`);
    }

    const visibleMat = new ShaderMaterial(
      mesh.name + '_faceColor_visible',
      this.scene,
      { vertex: 'faceColor', fragment: 'faceColor' },
      { attributes: ['position', 'normal'], uniforms: ['world', 'viewProjection'] }
    );

    const hiddenMat = visibleMat.clone(mesh.name + '_faceColor_hidden') as ShaderMaterial;

    // Visible pass: front faces
    visibleMat.backFaceCulling = true;

    // Hidden pass: back faces (we flip orientation during hidden RTT)
    hiddenMat.backFaceCulling = true;
    hiddenMat.disableDepthWrite = true;
    hiddenMat.depthFunction = Engine.LEQUAL;

    // Track mats
    this.overlays.set(mesh, { vis: visibleMat, hid: hiddenEdges ? hiddenMat : undefined });

    // Render lists
    if (this.rttVisible.renderList!.indexOf(mesh) === -1) {
      this.rttVisible.renderList!.push(mesh);
    }
    if (hiddenEdges) {
      if (this.rttHidden.renderList!.indexOf(mesh) === -1) {
        this.rttHidden.renderList!.push(mesh);
      }
    } else {
      // Ensure not in hidden list
      const idx = this.rttHidden.renderList!.indexOf(mesh);
      if (idx !== -1) this.rttHidden.renderList!.splice(idx, 1);
      this.rttHidden.setMaterialForRendering(mesh, undefined);
    }

    // Hidden set
    if (hiddenEdges) this.hiddenMeshes.add(mesh); else this.hiddenMeshes.delete(mesh);

    // Remove any previous observer for this mesh to avoid stacking
    const prevObs = this.beforeRenderObsByMesh.get(mesh);
    if (prevObs) {
      mesh.onBeforeRenderObservable.remove(prevObs);
      this.beforeRenderObsByMesh.delete(mesh);
    }

    // Per-mesh uniform push (world/VP)
    const obs = mesh.onBeforeRenderObservable.add(() => {
      const cam = this.scene.activeCamera;
      if (!cam) return;

      const world = mesh.getWorldMatrix();
      const vp = cam.getViewMatrix().multiply(cam.getProjectionMatrix());

      visibleMat.setMatrix('world', world);
      visibleMat.setMatrix('viewProjection', vp);

      hiddenMat.setMatrix('world', world);
      hiddenMat.setMatrix('viewProjection', vp);
    });
    this.beforeRenderObsByMesh.set(mesh, obs);

    // Bind RTT materials
    this.rttVisible.setMaterialForRendering(mesh, visibleMat);
    if (hiddenEdges) {
      this.rttHidden.setMaterialForRendering(mesh, hiddenMat);
    } else {
      this.rttHidden.setMaterialForRendering(mesh, undefined);
    }

    // Hide/show main shaded mesh via layerMask, with restore
    const HIDDEN_MASK = 0x20000000;
    if (hideMesh) {
      if (!this.originalLayerMaskByMesh.has(mesh)) {
        this.originalLayerMaskByMesh.set(mesh, mesh.layerMask);
      }
      if (!this.originalVisibilityByMesh.has(mesh)) {
        this.originalVisibilityByMesh.set(mesh, mesh.visibility);
      }
      mesh.layerMask = HIDDEN_MASK;
    } else {
      const origMask = this.originalLayerMaskByMesh.get(mesh);
      if (origMask !== undefined) {
        mesh.layerMask = origMask;
        this.originalLayerMaskByMesh.delete(mesh);
      }
      const origVis = this.originalVisibilityByMesh.get(mesh);
      if (origVis !== undefined) {
        mesh.visibility = origVis;
        this.originalVisibilityByMesh.delete(mesh);
      }
    }
  }

  /**
   * Remove a mesh from the edge renderer
   */
  public remove(mesh: Mesh) {
    // Restore original layer mask / visibility if we modified them
    try {
      const origMask = this.originalLayerMaskByMesh.get(mesh);
      if (origMask !== undefined) {
        mesh.layerMask = origMask;
        this.originalLayerMaskByMesh.delete(mesh);
      }
      const origVis = this.originalVisibilityByMesh.get(mesh);
      if (origVis !== undefined) {
        mesh.visibility = origVis;
        this.originalVisibilityByMesh.delete(mesh);
      } else {
        mesh.visibility = mesh.visibility;
      }
    } catch (e) {
      // no-op
    }

    // Clear RTT material associations to prevent lingering draws
    try { this.rttVisible.setMaterialForRendering(mesh, undefined); } catch (e) {}
    try { this.rttHidden.setMaterialForRendering(mesh, undefined); } catch (e) {}

    // Remove from render lists
    try {
      if (this.rttVisible.renderList) {
        const i = this.rttVisible.renderList.indexOf(mesh);
        if (i !== -1) this.rttVisible.renderList.splice(i, 1);
      }
      if (this.rttHidden.renderList) {
        const i = this.rttHidden.renderList.indexOf(mesh);
        if (i !== -1) this.rttHidden.renderList.splice(i, 1);
      }
    } catch (e) {
      console.warn('EdgeRemove: error removing mesh from rtt.renderList', e);
    }

    // Remove the per-mesh observer
    const obs = this.beforeRenderObsByMesh.get(mesh);
    if (obs) {
      try { mesh.onBeforeRenderObservable.remove(obs); } catch (e) {}
      this.beforeRenderObsByMesh.delete(mesh);
    }

    // Dispose materials
    const entry = this.overlays.get(mesh);
    if (entry) {
      try { entry.vis.dispose(); } catch (e) {
        console.warn('EdgeRemove: error disposing overlay material (vis)', e);
      }
      try { entry.hid?.dispose(); } catch (e) {
        console.warn('EdgeRemove: error disposing overlay material (hid)', e);
      }
      try { this.overlays.delete(mesh); } catch (e) {
        console.warn('EdgeRemove: failed to delete overlays map entry', e);
      }
    }

    try { this.hiddenMeshes.delete(mesh); } catch (e) {}
    try { mesh.setEnabled(true); } catch (e) {}
  }

  /**
   * Clear all edge bindings/materials from every mesh.
   * Call this when switching out of an edge mode (or between modes) to avoid lingering edges.
   */
  public clearAllEdges() {
    // Remove meshes we are tracking
    const meshes = Array.from(this.overlays.keys());
    for (const m of meshes) {
      try { this.remove(m); } catch (e) {}
    }

    // Extra safety: also clear any residual RTT bindings
    if (this.rttVisible?.renderList) {
      for (const m of [...this.rttVisible.renderList]) {
        try { this.rttVisible.setMaterialForRendering(m, undefined); } catch (e) {}
      }
      this.rttVisible.renderList.length = 0;
    }
    if (this.rttHidden?.renderList) {
      for (const m of [...this.rttHidden.renderList]) {
        try { this.rttHidden.setMaterialForRendering(m, undefined); } catch (e) {}
      }
      this.rttHidden.renderList.length = 0;
    }

    // Remove any leftover per-mesh observers (for meshes we didn't track, just in case)
    try {
      // Not enumerable by mesh, but we cleared tracked ones above.
    } catch {}

    this.hiddenMeshes.clear();
  }

  /**
   * Dispose the EdgeRenderer and all associated resources
   */
  public dispose() {
    try { this.clearAllEdges(); } catch (e) {}

    // Dispose overlay mats (should be empty after clearAllEdges, but keep safe)
    this.overlays.forEach((mats) => {
      try { mats.vis.dispose(); } catch (e) {
        console.warn('EdgeRenderer.dispose: error disposing overlay material', e);
      }
      try { mats.hid?.dispose(); } catch (e) {
        console.warn('EdgeRenderer.dispose: error disposing hidden overlay material', e);
      }
    });
    this.overlays.clear();

    this.hiddenMeshes.clear();
    this.originalLayerMaskByMesh = new WeakMap<Mesh, number>();
    this.originalVisibilityByMesh = new WeakMap<Mesh, number>();

    // Remove resize observer
    try {
      if (this.resizeObserver) {
        this.engine.onResizeObservable.remove(this.resizeObserver);
        this.resizeObserver = null;
      }
    } catch (e) {}

    // Remove RTTs from scene and dispose
    if (this.rttVisible || this.rttHidden) {
      try {
        this.scene.customRenderTargets =
          this.scene.customRenderTargets.filter((rt) => rt !== this.rttVisible && rt !== this.rttHidden);
        this.rttVisible?.dispose();
        this.rttHidden?.dispose();
      } catch (e) {
        console.warn('EdgeRenderer.dispose: error disposing RTTs', e);
      }
    }

    // Dispose hidden depth texture explicitly
    try {
      if (this.rttHiddenDepthTexture && this.rttHiddenDepthTexture.dispose) {
        this.rttHiddenDepthTexture.dispose();
      }
    } catch (e) {
      console.warn('EdgeRenderer.dispose: error disposing depth texture', e);
    }

    if (this.postProcess) {
      try { this.postProcess.dispose(); } catch (e) {
        console.warn('EdgeRenderer.dispose: error disposing PostProcess', e);
      }
    }

    if (this.edgeRendererPipeline) {
      try {
        const cam = this.scene.activeCamera;
        if (cam) {
          this.scene.postProcessRenderPipelineManager.detachCamerasFromRenderPipeline(
            this.edgeRendererPipeline.name,
            cam
          );
        }
        this.scene.postProcessRenderPipelineManager.removePipeline(
          this.edgeRendererPipeline.name
        );
        this.edgeRendererPipeline.dispose();
      } catch (e) {
        console.warn('EdgeRenderer.dispose: error disposing pipeline', e);
      }
    }

    // Null out refs
    this.rttVisible = null as any;
    this.rttHidden = null as any;
    this.rttHiddenDepthTexture = null as any;
    this.postProcess = null as any;
    this.edgeRendererPipeline = null as any;
  }
}